import { useEffect, useRef } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { LoadingIndicator } from "@leagueplatform/web-common-components";
import { isMemberIneligible } from "api/get-app-init-details";
import { APP_MANAGED_ROUTES, SIGN_OUT } from "common/app-managed-routes";
import { BasePage } from "components/base-page/base-page.component";
import {
  ErrorState,
  ERROR_TYPES,
} from "components/error-state/error-state.component";
import { useOnboarding } from "hooks/use-onboarding/use-onboarding.hook";
import { usePlanSwitcherLoginGate } from "hooks/use-plan-switcher-login-gate/use-plan-switcher-login-gate.hook";
import { useAppInitDetails } from "hooks/use-app-init-details/use-app-init-details.hook";
import { LiveChat } from "live-chat/live-chat.component";

export const PrivateRoutesContainer = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { isOnboardingComplete, currentStep, isLoading, error } =
    useOnboarding();
  const { hasMultiplePlans, isLoading: isPlanSwitcherGateLoading } =
    usePlanSwitcherLoginGate();
  const { data: appInit } = useAppInitDetails();
  const location = useLocation();

  // Per-mount tracker: was the user on `/switch-account` at any point
  // during this authenticated session? PrivateRoutesContainer is rendered
  // by AuthSessionContainer only while `isAuthenticated === true`, and
  // React Router keeps parent-route elements mounted across child-route
  // changes — so this ref persists throughout the auth session and resets
  // on logout → re-login. That's how we get "show the Plan Switcher once
  // per login" without leaking state across sessions via storage
  // (Auth0's redirect preserves sessionStorage, so a storage-backed flag
  // would falsely suppress the gate on the next login).
  const hasVisitedSwitchAccountRef = useRef(false);
  const isOnSwitchAccountRoute =
    location.pathname === APP_MANAGED_ROUTES.SWITCH_ACCOUNT;
  useEffect(() => {
    if (isOnSwitchAccountRoute) {
      hasVisitedSwitchAccountRef.current = true;
    }
  }, [isOnSwitchAccountRoute]);

  // iOS parity (EMBLEM-7075):
  // `PostAuthenticationCoordinator.validateUserEligibility()` throws
  // when the backend flags the user as ineligible via the nested
  // `eligibility_info` block; the root coordinator then bounces the
  // member to the unauthenticated flow (no overlay). Web equivalent:
  // full sign-out so Auth0 tokens are cleared before the user re-enters
  // the IDP. Absence of `eligibility_info` — or a missing `eligible`
  // field inside it — is treated as eligible (see `isMemberIneligible`).
  // The `ERROR_TYPES.INELIGIBLE` overlay stays defined for a later
  // product decision; it just isn't wired here.
  const isIneligible = isMemberIneligible(appInit);
  useEffect(() => {
    if (isIneligible) {
      window.location.assign(SIGN_OUT);
    }
  }, [isIneligible]);

  const shouldRedirectToPlanSwitcher =
    !isPlanSwitcherGateLoading &&
    hasMultiplePlans &&
    !hasVisitedSwitchAccountRef.current &&
    !isOnSwitchAccountRoute;

  if (isLoading || isPlanSwitcherGateLoading) return <LoadingIndicator />;
  // Full-page overlay so the broken header/footer chrome isn't visible
  // behind the error when app-init fails.
  if (error)
    return (
      <ErrorState error={error} isFullPage errorType={ERROR_TYPES.API_ERROR} />
    );

  // Redirect above kicks in via effect after commit; hold on a loading
  // indicator so the authenticated shell doesn't flash between render
  // and navigation.
  if (isIneligible) return <LoadingIndicator />;

  // First render after login for members with more than one K-ID: send
  // them to the Plan Switcher before anything else. Runs ahead of
  // onboarding to match iOS's `PostAuthenticationCoordinator` ordering
  // (PolicySwitcherCoordinator precedes AllSetCoordinator; EMBLEM-7088).
  if (shouldRedirectToPlanSwitcher) {
    return <Navigate to={APP_MANAGED_ROUTES.SWITCH_ACCOUNT} replace />;
  }

  // Always let the Plan Switcher route itself render. Without this, a
  // gate-driven redirect that lands on `/switch-account` for a user with
  // incomplete onboarding would immediately get overlaid by an onboarding
  // step and the switcher would never appear.
  if (isOnSwitchAccountRoute) {
    return <BasePage>{children as JSX.Element}</BasePage>;
  }

  if (!isOnboardingComplete && currentStep) return currentStep.component;

  return (
    <BasePage>
      {children as JSX.Element}
      <LiveChat />
    </BasePage>
  );
};
